import React from 'react';
import ReactDom from 'react-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import transformJob from './transformJob';

export interface TemplateProperties {
  job: ReturnType<typeof transformJob>;
  locale: string;
  applyUrl: string | null;
  profiles: Record<string, unknown>;
}

export type TemplatesProperty = React.ComponentType<TemplateProperties>
| React.ElementType<TemplateProperties>
| Record<string, unknown> | undefined;

type TemplateElementType = React.ElementType<TemplateProperties> & {
  propsDescription: {
    name: string;
    label: string;
    expectedType?: string;
    custom?: unknown;
    tooltip?: string;
  }[];
};

export interface TemplatesProperties {
  CompanyDescription: TemplateElementType;
  Description: TemplateElementType;
  properties?: Record<string, unknown>;
  [key: keyof TemplateDependencies]: TemplatesProperty;
}

const templates: Record<string, TemplatesProperties | undefined> = {};

const getTemplateKey = (
  sourceName: string,
  revision: string,
): string => `${
  sourceName}-${revision}`;

export interface TemplateDependencies {
  react: typeof React;
  'react-dom': typeof ReactDom;
  'prop-types': typeof PropTypes;
  'styled-components': typeof styled;
  [key: string]: unknown;
}

export const templateDependencies: TemplateDependencies = {
  react: React,
  'react-dom': ReactDom,
  'prop-types': PropTypes,
  'styled-components': styled,
};

const require = (path: string) => templateDependencies[path];

const fetchTemplate = async (
  sourceName: string,
  revision: string,
  origin: string,
): Promise<TemplatesProperties> => {
  const result = await fetch(
    `${origin}/job-templates/${encodeURI(sourceName)}.js?v=${revision}`,
  );

  // eslint-disable-next-line @typescript-eslint/no-implied-eval
  return new Function('require', `return ${await result.text()}`)(require);
};

const importJobTemplate = async (
  sourceName: string,
  revision: string,
  origin: string,
) => {
  const templateKey = getTemplateKey(sourceName, revision);
  const cachedTemplate: TemplatesProperties
  | undefined = templates[templateKey];

  if (cachedTemplate) {
    return cachedTemplate;
  }

  const fetchedTemplate = fetchTemplate(sourceName, revision, origin);

  templates[templateKey] = fetchedTemplate as unknown as TemplatesProperties;

  return fetchedTemplate;
};

export default importJobTemplate;
